import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Viewer,
  KeyboardEventModifier,
} from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import EventEmitter, { EventEmitterInterface } from "./EventEmitter";

export default class ScreenSpaceEventsPlugin
  implements AttachablePluginInterface
{
  private handlers: ScreenSpaceEventHandler[];
  private _leftClickEvent =
    new EventEmitter<ScreenSpaceEventHandler.PositionedEvent>();
  private _leftDownEvent =
    new EventEmitter<ScreenSpaceEventHandler.PositionedEvent>();
  private _leftUpEvent =
    new EventEmitter<ScreenSpaceEventHandler.PositionedEvent>();
  private _shiftLeftClickEvent =
    new EventEmitter<ScreenSpaceEventHandler.PositionedEvent>();
  private _mouseMoveEvent =
    new EventEmitter<ScreenSpaceEventHandler.MotionEvent>();

  get leftClickEvent(): EventEmitterInterface<ScreenSpaceEventHandler.PositionedEvent> {
    return this._leftClickEvent;
  }
  get leftDownEvent(): EventEmitterInterface<ScreenSpaceEventHandler.PositionedEvent> {
    return this._leftDownEvent;
  }
  get leftUpEvent(): EventEmitterInterface<ScreenSpaceEventHandler.PositionedEvent> {
    return this._leftUpEvent;
  }
  get shiftLeftClickEvent(): EventEmitterInterface<ScreenSpaceEventHandler.PositionedEvent> {
    return this._shiftLeftClickEvent;
  }
  get mouseMoveEvent(): EventEmitterInterface<ScreenSpaceEventHandler.MotionEvent> {
    return this._mouseMoveEvent;
  }

  setViewer(viewer: Viewer) {
    this.detachViewer();
    if (!viewer) return;

    // Disable Cesium Viewer's default picking handlers. This app emits its own
    // screen-space events below, and the default Viewer LEFT_CLICK handler calls
    // scene.pick on mouse up which can crash while clipping planes/tilesets are
    // being rebuilt.
    viewer.screenSpaceEventHandler.removeInputAction(
      ScreenSpaceEventType.LEFT_CLICK,
    );
    viewer.screenSpaceEventHandler.removeInputAction(
      ScreenSpaceEventType.LEFT_DOUBLE_CLICK,
    );

    this.handlers = [
      this.attachHandler(
        viewer,
        this._leftClickEvent,
        ScreenSpaceEventType.LEFT_CLICK,
      ),
      this.attachHandler(
        viewer,
        this._leftDownEvent,
        ScreenSpaceEventType.LEFT_DOWN,
      ),
      this.attachHandler(
        viewer,
        this._leftUpEvent,
        ScreenSpaceEventType.LEFT_UP,
      ),
      this.attachHandler(
        viewer,
        this._shiftLeftClickEvent,
        ScreenSpaceEventType.LEFT_CLICK,
        KeyboardEventModifier.SHIFT,
      ),
      this.attachHandler(
        viewer,
        this._mouseMoveEvent,
        ScreenSpaceEventType.MOUSE_MOVE,
      ),
    ];
  }

  detachViewer() {
    if (this.handlers) {
      this.handlers.forEach((h) => {
        h.destroy();
      });
    }
    this.handlers = null;
  }

  private attachHandler<T>(
    viewer: Viewer,
    event: EventEmitter<T>,
    type: ScreenSpaceEventType,
    modifier?: KeyboardEventModifier,
  ) {
    const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction(
      (target) => {
        event.emit(target);
      },
      type,
      modifier,
    );
    return handler;
  }
}
